import { type GitlabProvisioningManageResult } from 'network/httpClients/GitlabClient';
import { InstallationStatusMessage } from 'shared/InstallationDialog';
import { ScopeResultList } from './ScopeResultList';

export type GitlabManageResultsProps = {
  /** Whether the run is still in flight (non-terminal). */
  isPending: boolean;
  /** Single-line message shown in the neutral status box while the run is in flight. */
  pendingMessage: string;
  bannerStatus?: 'success' | 'error' | 'warning';
  bannerMessage?: string;
  retryLabel?: string;
  onRetry?: () => void;
  results: GitlabProvisioningManageResult[];
  /** Resolves the right-hand status label for a per-scope row (i18n lives in the container). */
  getResultStatusLabel: (result: GitlabProvisioningManageResult) => string;
  /** Resolves the left-hand scope label (defaults to the raw scope). */
  getResultLabel?: (result: GitlabProvisioningManageResult) => string;
  /** Show a group/repo icon before each label. */
  showKindIcon?: boolean;
};

/**
 * Shared page-3 body for the v2 GitLab wizard. A single persistent status box sits over the
 * per-scope list and changes state in place rather than swapping elements: neutral while the
 * run is in flight (spinner + `pendingMessage`), then success / warning / error (with an
 * optional Retry) once terminal. Each row streams live (in_progress spinner, queued muted,
 * terminal colored). Install (add-only) and manage (add + remove) both render it; the
 * container supplies all copy.
 */
export const GitlabManageResults = ({
  isPending,
  pendingMessage,
  bannerStatus,
  bannerMessage,
  retryLabel,
  onRetry,
  results,
  getResultStatusLabel,
  getResultLabel = (result) => result.scope,
  showKindIcon = false,
}: GitlabManageResultsProps) => {
  const hasResults = results.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {isPending ? (
        <InstallationStatusMessage status="pending" message={pendingMessage} />
      ) : (
        bannerStatus &&
        bannerMessage && (
          <InstallationStatusMessage
            status={bannerStatus}
            message={bannerMessage}
            retryLabel={retryLabel}
            onRetry={onRetry}
          />
        )
      )}

      {hasResults && (
        <ScopeResultList
          results={results}
          showKindIcon={showKindIcon}
          getResultStatusLabel={getResultStatusLabel}
          getResultLabel={getResultLabel}
        />
      )}
    </div>
  );
};
